import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11 } from "styled-components";
import { useRouter } from "next/router";
import React from "react";
// import { up } from "~/features/app/screens";
import { clearRouterPath, hasItems, takeFirst } from "~/features/app/utils";
import { DistributorsFilters, distributorsFiltersFragment } from "~/features/blocks/inventory/distributors/distributors-filters";
import { DistributorsMap } from "~/features/blocks/inventory/distributors/distributors-map";
import { DistributorCard } from "~/features/card/distributor-card";
import { NoResults } from "~/features/filter/no-results";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
import { Slider } from "~/features/ui/slider";
import { Progress } from "~/features/ui/slider/progress";
import { Wrap } from "~/features/ui/wrap";
import { makeFragmentData, type FragmentType } from "~/gql/runtime";
import { type DistributorCardFragment, type DistributorsFiltersFragment } from "~/gql/graphql";
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
export type DistributorsBlockViewProps = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  filters?: FragmentType<typeof distributorsFiltersFragment> | null;
  hasTopDivider?: boolean | null;
  industries?: Array<{
    id?: string | null;
  }> | null;
  initialDistributors?: DistributorCardFragment[] | null;
  mode?: string | null;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  theme?: string | null;
};
export function DistributorsBlockView(props: DistributorsBlockViewProps) {
  const {
    mode = "light",
    theme = "white",
    anchor,
    filters,
    initialDistributors = [],
    hasTopDivider = true,
    sectionProps
  } = props;
  const themeStyle = getThemeStyle({
    mode: mode as Mode,
    theme: theme as Theme
  });
  const router = useRouter();
  const activeCountry = router.query.country as string || "";
  const activeState = router.query.state as string || "";
  const filteredFallbackDistributors = React.useMemo(() => {
    if (!activeCountry && !activeState) {
      return (initialDistributors ?? []) as DistributorCardFragment[];
    }
    const matchesFilters = (distributor: DistributorCardFragment) => {
      if (!distributor.locations || distributor.locations.length === 0) {
        return false;
      }
      return distributor.locations.some(location => {
        const parts = location?.location?.parts;
        if (!parts) {
          return false;
        }
        const countryMatches = activeCountry ? parts.country === activeCountry : true;
        const stateMatches = activeState ? parts.state === activeState : true;
        return countryMatches && stateMatches;
      });
    };
    return ((initialDistributors ?? []) as DistributorCardFragment[]).filter(matchesFilters);
  }, [initialDistributors, activeCountry, activeState]);

  // Distributors are loaded server-side and filtered client-side by country
  // and state.
  const distributors = filteredFallbackDistributors;
  const filtersData = React.useMemo(() => {
    if (filters) {
      return filters;
    }
    const filtersFragment: DistributorsFiltersFragment = {
      distributors: distributors.map(distributor => ({
        locations: distributor.locations ?? []
      }))
    };
    return makeFragmentData(filtersFragment, distributorsFiltersFragment);
  }, [filters, distributors]);
  const onChange = (filterType: string, e: string) => {
    const queryParams = {
      ...router.query
    };
    if (filterType === "Country") {
      queryParams.country = e;
      queryParams.state = "";
    }
    if (filterType === "State") {
      queryParams.state = e;
    }
    router.push({
      pathname: clearRouterPath(router.asPath),
      query: queryParams
    }, undefined, {
      shallow: true,
      scroll: false
    });
  };
  function onClearFilters() {
    const {
      country,
      state,
      ...rest
    } = router.query;
    router.push({
      pathname: clearRouterPath(router.asPath),
      query: rest
    }, undefined, {
      shallow: true,
      scroll: false
    });
  }
  const desktopDistributorRefs = React.useRef<{
    [key: string]: HTMLDivElement | null;
  }>({});
  const mobileDistributorRefs = React.useRef<{
    [key: string]: HTMLDivElement | null;
  }>({});
  const [activeDistributor, setActiveDistributor] = React.useState<DistributorCardFragment | null>(null);
  const [isZoomed, setIsZoomed] = React.useState(false);
  const scrollToDistributor = (distributor: DistributorCardFragment) => {
    if (distributor.id) {
      setActiveDistributor(distributor);
      const isMobile = window.innerWidth < 1024; // 1024px is Tailwind's lg breakpoint
      const refs = isMobile ? mobileDistributorRefs : desktopDistributorRefs;
      if (refs.current[distributor.id]) {
        refs.current[distributor.id]?.scrollIntoView({
          behavior: "smooth",
          block: "center"
        });
      }
    }
  };
  const scrollToMap = (distributor: DistributorCardFragment) => {
    if (distributor.id) {
      const mapRef = document.querySelector("#distributors-map");
      if (mapRef) {
        mapRef.scrollIntoView({
          behavior: "smooth",
          block: "center"
        });
      }
    }
  };
  const [activeSlideIndex, setActiveSlideIndex] = React.useState(0);
  return <>
      <DistributorsFilters mode={mode as Mode} theme={theme as Theme} {...filtersData} onChange={onChange} label="Show" value={{
      country: activeCountry,
      state: activeState
    }} />
      <_StyledSection {...sectionProps} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[hasTopDivider && {
      "borderTopWidth": "1px"
    }, themeStyle.text, themeStyle.border, themeStyle.background]}>
        <Wrap mode={mode as Mode}>
          <_StyledDiv>
            <_StyledDiv2>
              <_StyledDiv3 className="hide-scrollbar" $_css2={[{
              "display": "none",
              "height": "100%",
              "minHeight": "100%",
              "maxWidth": "33svw",
              "flexDirection": "row",
              "> :not([hidden]) ~ :not([hidden])": {
                "--tw-divide-y-reverse": "0",
                "borderTopWidth": "calc(1px * calc(1 - var(--tw-divide-y-reverse)))",
                "borderBottomWidth": "calc(1px * var(--tw-divide-y-reverse))"
              },
              "overflowY": "scroll",
              "@media (min-width: 1024px)": {
                "display": "flex",
                "flex": "1 1 0%",
                "flexDirection": "column",
                "borderLeftWidth": "1px"
              }
            }, themeStyle.border]}>
                {!hasItems(distributors) ? <_StyledDiv4 $_css3={[{
                "gridColumn": "1 / -1",
                "borderTopWidth": "1px",
                "paddingLeft": "4rem",
                "paddingRight": "4rem",
                "paddingTop": "4rem",
                "paddingBottom": "4rem"
              }, themeStyle.border]}>
                    <NoResults onClear={onClearFilters} />
                  </_StyledDiv4> : distributors.map((distributor, idx) => <_StyledDiv5 ref={el => {
                desktopDistributorRefs.current[distributor.id as string] = el;
              }} key={`desktop-${idx}`} $_css4={[{
                "gridColumn": "span 4 / span 4"
              }, themeStyle.border, idx === distributors.length - 1 && {
                "borderBottomWidth": "1px"
              }]}>
                      <DistributorCard onClick={() => {
                  if (distributor.locations.length === 1) {
                    setIsZoomed(true);
                  }
                  scrollToMap(distributor);
                  setActiveDistributor(distributor);
                }} highlighted={activeDistributor?.id === distributor.id} theme={theme as Theme} mode={mode as Mode} {...distributor} />
                    </_StyledDiv5>)}
              </_StyledDiv3>
              <_StyledDiv6>
                <_StyledDiv7 $_css5={[{
                "position": "relative",
                "zIndex": "10",
                "borderBottomWidth": "1px"
              }, themeStyle.border, themeStyle.background]}>
                  <Slider options={{
                  fixedWidth: "90%"
                }} hasTrack onMove={splide => setActiveSlideIndex(splide.Components.Controller.getIndex())}>
                    {distributors.map((distributor, idx) => <_StyledSliderSlide key={`mobile-${idx}`} $_css6={[{
                    "gridColumn": "span 4 / span 4",
                    "@media (min-width: 1024px)": {
                      "borderRightWidth": "1px",
                      "outlineStyle": "solid",
                      "outlineWidth": "1px"
                    }
                  }, themeStyle.outline, themeStyle.border]}>
                        <DistributorCard ref={el => {
                      mobileDistributorRefs.current[distributor.id as string] = el;
                    }} onClick={() => {
                      scrollToMap(distributor);
                      if (distributor.locations.length === 1) {
                        setIsZoomed(true);
                      }
                      setActiveSlideIndex(idx);
                      setActiveDistributor(distributor);
                    }} highlighted={activeDistributor?.id === distributor.id} theme={theme as Theme} mode={mode as Mode} {...distributor} />
                      </_StyledSliderSlide>)}
                  </Slider>
                </_StyledDiv7>
                <_StyledDiv8 $_css7={[{
                "position": "relative",
                "zIndex": "10",
                "paddingLeft": "2.4rem",
                "paddingRight": "2.4rem",
                "paddingTop": "3.2rem",
                "paddingBottom": "3.2rem"
              }, themeStyle.background]}>
                  <Progress activeSlideIndex={activeSlideIndex} totalSlides={distributors.length} />
                </_StyledDiv8>
              </_StyledDiv6>
              <_StyledDiv9 $_css8={[{
              "top": "0",
              "height": "100%",
              "width": "100%",
              "borderTopWidth": "1px",
              "@media (min-width: 1024px)": {
                "position": "sticky",
                "flex": "2",
                "borderLeftWidth": "1px",
                "borderRightWidth": "1px",
                "borderTopWidth": "0px"
              }
            }, themeStyle.border]}>
                <DistributorsMap distributors={distributors} onDistributorClick={scrollToDistributor} isZoomed={isZoomed} setIsZoomed={setIsZoomed} activeCountry={activeCountry} activeDistributor={activeDistributor} />
              </_StyledDiv9>
            </_StyledDiv2>
          </_StyledDiv>
        </Wrap>
      </_StyledSection>
    </>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-6wymtv-0"
})`${p => p.$_css}`;
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-6wymtv-1"
})({
  "@media (min-width: 1024px)": {
    "paddingLeft": "4rem",
    "paddingRight": "4rem"
  }
});
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-6wymtv-2"
})({
  "position": "relative",
  "display": "flex",
  "flexDirection": "column",
  "@media (min-width: 1024px)": {
    "height": "max-content",
    "flexDirection": "row"
  }
});
var _StyledDiv3 = _styled4("div").withConfig({
  componentId: "sc-sf-6wymtv-3"
})`${p => p.$_css2}`;
var _StyledDiv4 = _styled5("div").withConfig({
  componentId: "sc-sf-6wymtv-4"
})`${p => p.$_css3}`;
var _StyledDiv5 = _styled6("div").withConfig({
  componentId: "sc-sf-6wymtv-5"
})`${p => p.$_css4}`;
var _StyledDiv6 = _styled7("div").withConfig({
  componentId: "sc-sf-6wymtv-6"
})({
  "@media (min-width: 1024px)": {
    "display": "none"
  }
});
var _StyledDiv7 = _styled8("div").withConfig({
  componentId: "sc-sf-6wymtv-7"
})`${p => p.$_css5}`;
var _StyledSliderSlide = _styled9(Slider.Slide).withConfig({
  componentId: "sc-sf-6wymtv-8"
})`${p => p.$_css6}`;
var _StyledDiv8 = _styled10("div").withConfig({
  componentId: "sc-sf-6wymtv-9"
})`${p => p.$_css7}`;
var _StyledDiv9 = /*#__PURE__*/_styled11("div").withConfig({
  displayName: "distributors___StyledDiv9",
  componentId: "sc-111pytu-0"
})(["", ""], p => p.$_css8);